import type { Identifier } from "ra-core";

import {
  PROFILE_BEDROOM_OPTIONS,
  PROFILE_INTEREST_LEVELS,
  UNIT_TYPES,
  type Deal,
  type DealProfile,
  type ProfileBedrooms,
  type ProfileInterestLevel,
  type ProfileOption,
  type Project,
  type UnitType,
} from "../types";

/**
 * Almikia Epic EF, DATA CONTRACT EF (8): the single pure helper module shared
 * by the UI (TASK-119 to TASK-122), the FakeRest emulation
 * (`providers/fakerest/dealProfilesProvider.ts`) and the CSV exporter -- no
 * I/O, no React, linear in catalogue size (the ticket's own performance
 * requirement). Covers the Spanish validators of (3), the immutable
 * single/multi-select toggles, the picker option lists and "{n} de 15" count
 * of (6), and the CSV row mapping of (7).
 */

// ---------------------------------------------------------------------------
// Fixed value sets / labels (re-exported here for a single import point)
// ---------------------------------------------------------------------------

/** DATA CONTRACT EF (2): `property_type` reuses `UNIT_TYPES`, never a copy. */
export const PROFILE_PROPERTY_TYPES = UNIT_TYPES;
export type ProfilePropertyType = UnitType;

export const PROFILE_PROPERTY_TYPE_LABELS: Record<ProfilePropertyType, string> =
  {
    depto: "Departamento",
    local: "Local",
    lote: "Lote",
  };

export const PROFILE_BEDROOM_LABELS: Record<ProfileBedrooms, string> = {
  0: "Estudio",
  1: "1",
  2: "2",
  3: "3",
  4: "4 o más",
};

export const PROFILE_INTEREST_LEVEL_LABELS: Record<
  ProfileInterestLevel,
  string
> = {
  alto: "Alto",
  medio: "Medio",
  bajo: "Bajo",
};

export { PROFILE_BEDROOM_OPTIONS, PROFILE_INTEREST_LEVELS };

// ---------------------------------------------------------------------------
// (3) Shared guard messages (identical Spanish text as the SQL side)
// ---------------------------------------------------------------------------

export const OPTION_NOT_FOUND_MESSAGE = "Esta opción no existe";
export const OPTION_ARCHIVED_MESSAGE = "Esta opción está archivada";
export const PROJECT_ARCHIVED_MESSAGE = "Este proyecto está archivado";
export const DUPLICATE_ELEMENT_MESSAGE = "No puedes repetir una opción";
export const MAX_OPTIONS_MESSAGE = "Puedes elegir hasta 10 opciones";
export const DEAL_ID_IMMUTABLE_MESSAGE =
  "No puedes cambiar el lead de este perfil";
export const PROFILE_ALREADY_EXISTS_MESSAGE = "Este lead ya tiene un perfil";
export const KEY_IMMUTABLE_MESSAGE =
  "No puedes cambiar la clave interna de una opción";
export const LABEL_DUPLICATE_MESSAGE = "Ya existe una opción con ese nombre";
export const LABEL_REQUIRED_MESSAGE = "Escribe el nombre de la opción";
export const LABEL_TOO_LONG_MESSAGE =
  "El nombre puede tener hasta 60 caracteres";
export const AMOUNT_RANGE_MESSAGE =
  "El monto debe estar entre $0 y $999,999,999.99";
export const BUDGET_ORDER_MESSAGE =
  "El presupuesto mínimo no puede ser mayor que el máximo";
export const ZONE_TOO_LONG_MESSAGE = "La zona puede tener hasta 120 caracteres";
export const NOTES_TOO_LONG_MESSAGE =
  "Las notas del perfil pueden tener hasta 2000 caracteres";
export const LEAD_WRITE_DENIED_MESSAGE =
  "No puedes editar el perfil de este lead";

export const MAX_ARRAY_OPTIONS = 10;
export const MAX_LABEL_LENGTH = 60;
export const MAX_ZONE_LENGTH = 120;
export const MAX_NOTES_LENGTH = 2000;
export const MAX_AMOUNT = 999_999_999.99;

const isBlank = (value: string | null | undefined): boolean =>
  value == null || value.trim() === "";

// ---------------------------------------------------------------------------
// (1) Catalogue option label validator (direccion-only catalogue writes)
// ---------------------------------------------------------------------------

/**
 * DATA CONTRACT EF (1): a catalogue option's `label` -- non-blank, at most 60
 * chars, unique case-insensitively among the SAME table's non-archived rows
 * (an insert, a rename, or a reactivation all run this check).
 */
export function assertValidOptionLabel(
  label: unknown,
  existingActiveLabels: ReadonlyArray<{
    id: Identifier;
    label: string;
    archived_at?: string | null;
  }>,
  selfId: Identifier | undefined,
): void {
  if (typeof label !== "string" || label.trim() === "") {
    throw new Error(LABEL_REQUIRED_MESSAGE);
  }
  if (label.trim().length > MAX_LABEL_LENGTH) {
    throw new Error(LABEL_TOO_LONG_MESSAGE);
  }
  const normalized = label.trim().toLowerCase();
  const duplicate = existingActiveLabels.some(
    (row) =>
      String(row.id) !== String(selfId) &&
      row.archived_at == null &&
      row.label.trim().toLowerCase() === normalized,
  );
  if (duplicate) {
    throw new Error(LABEL_DUPLICATE_MESSAGE);
  }
}

/** DATA CONTRACT EF (1): a UI-created option gets its table's current max position + 10. */
export function computeNextPosition(
  existing: ReadonlyArray<{ position: number }>,
): number {
  if (existing.length === 0) return 10;
  return Math.max(...existing.map((option) => option.position)) + 10;
}

// ---------------------------------------------------------------------------
// (3) Shape validators for `DealProfile` fields
// ---------------------------------------------------------------------------

/** DATA CONTRACT EF (2)/(3): an amount is NULL or between 0 and 999,999,999.99. */
export function assertValidAmount(value: unknown): void {
  if (value == null) return;
  if (
    typeof value !== "number" ||
    Number.isNaN(value) ||
    value < 0 ||
    value > MAX_AMOUNT
  ) {
    throw new Error(AMOUNT_RANGE_MESSAGE);
  }
}

/** DATA CONTRACT EF (2)/(3): `budget_min` <= `budget_max` when both are set. */
export function assertValidBudgetRange(min: unknown, max: unknown): void {
  assertValidAmount(min);
  assertValidAmount(max);
  if (typeof min === "number" && typeof max === "number" && min > max) {
    throw new Error(BUDGET_ORDER_MESSAGE);
  }
}

/** DATA CONTRACT EF (2)/(3): `zone` at most 120 chars when set. */
export function assertValidZone(zone: unknown): void {
  if (zone == null) return;
  if (typeof zone === "string" && zone.trim().length > MAX_ZONE_LENGTH) {
    throw new Error(ZONE_TOO_LONG_MESSAGE);
  }
}

/** DATA CONTRACT EF (2)/(3): `notes` at most 2000 chars when set. */
export function assertValidNotes(notes: unknown): void {
  if (notes == null) return;
  if (typeof notes === "string" && notes.trim().length > MAX_NOTES_LENGTH) {
    throw new Error(NOTES_TOO_LONG_MESSAGE);
  }
}

// ---------------------------------------------------------------------------
// (3) Single-select / multi-select reference guards
// ---------------------------------------------------------------------------

interface ArchivableRow {
  id: Identifier;
  archived_at?: string | null;
}

/**
 * DATA CONTRACT EF (3): a single-select id that does not exist in `catalogue`
 * -> "Esta opción no existe"; a NEWLY set id (changed from `previousId`) that
 * is archived -> "Esta opción está archivada". An id already held survives
 * any unrelated update.
 */
export function assertSingleSelectGuard(
  newId: Identifier | null | undefined,
  previousId: Identifier | null | undefined,
  catalogue: ReadonlyArray<ArchivableRow>,
): void {
  if (newId == null) return;
  const row = catalogue.find(
    (candidate) => String(candidate.id) === String(newId),
  );
  if (!row) {
    throw new Error(OPTION_NOT_FOUND_MESSAGE);
  }
  const isNew = String(newId) !== String(previousId ?? "");
  if (isNew && row.archived_at != null) {
    throw new Error(OPTION_ARCHIVED_MESSAGE);
  }
}

export interface ArraySelectGuardOptions<RowType extends ArchivableRow> {
  notFoundMessage?: string;
  archivedMessage?: string;
  isArchived?: (row: RowType) => boolean;
}

/**
 * DATA CONTRACT EF (3): every array-select guard, in order -- a duplicate
 * element -> "No puedes repetir una opción"; more than 10 elements ->
 * "Puedes elegir hasta 10 opciones"; an unknown id -> `notFoundMessage`; a
 * NEWLY added id (absent from `previousIds`) that is archived ->
 * `archivedMessage`. Reused for the three catalogue arrays (default
 * messages, `ProfileOption`'s own `archived_at`) and `compared_project_ids`
 * (project-specific messages/`isArchived`, `Project.archived`).
 */
export function assertArraySelectGuards<RowType extends ArchivableRow>(
  newIds: readonly Identifier[],
  previousIds: readonly Identifier[],
  catalogue: ReadonlyArray<RowType>,
  options: ArraySelectGuardOptions<RowType> = {},
): void {
  const notFoundMessage = options.notFoundMessage ?? OPTION_NOT_FOUND_MESSAGE;
  const archivedMessage = options.archivedMessage ?? OPTION_ARCHIVED_MESSAGE;
  const isArchived =
    options.isArchived ?? ((row: RowType) => row.archived_at != null);

  const seen = new Set<string>();
  for (const id of newIds) {
    const key = String(id);
    if (seen.has(key)) {
      throw new Error(DUPLICATE_ELEMENT_MESSAGE);
    }
    seen.add(key);
  }
  if (newIds.length > MAX_ARRAY_OPTIONS) {
    throw new Error(MAX_OPTIONS_MESSAGE);
  }

  const byId = new Map(catalogue.map((row) => [String(row.id), row]));
  const previousSet = new Set(previousIds.map((id) => String(id)));
  for (const id of newIds) {
    const row = byId.get(String(id));
    if (!row) {
      throw new Error(notFoundMessage);
    }
    const isNew = !previousSet.has(String(id));
    if (isNew && isArchived(row)) {
      throw new Error(archivedMessage);
    }
  }
}

/** The 5 catalogue lists plus `projects`, as `assertValidDealProfileWrite` needs them. */
export interface DealProfileCatalogues {
  buyerTypes: readonly ProfileOption[];
  creditTypes: readonly ProfileOption[];
  timelines: readonly ProfileOption[];
  motivators: readonly ProfileOption[];
  objections: readonly ProfileOption[];
  projects: readonly Project[];
}

/**
 * DATA CONTRACT EF (3): every shape + reference guard on a `deal_profiles`
 * write, shape rules first (the contract's own ordering: "the UI and
 * FakeRest validate them first"). `previous` is `undefined` on create --
 * every id/array element in `data` then counts as "newly set". Only the
 * fields actually present in `data` are checked, mirroring "each save sends
 * only the changed field(s)".
 */
export function assertValidDealProfileWrite(
  data: Partial<DealProfile>,
  previous: Partial<DealProfile> | undefined,
  catalogues: DealProfileCatalogues,
): void {
  if ("budget_min" in data || "budget_max" in data) {
    assertValidBudgetRange(
      "budget_min" in data ? data.budget_min : previous?.budget_min,
      "budget_max" in data ? data.budget_max : previous?.budget_max,
    );
  }
  if ("down_payment" in data) {
    assertValidAmount(data.down_payment);
  }
  if ("monthly_payment" in data) {
    assertValidAmount(data.monthly_payment);
  }
  if ("zone" in data) {
    assertValidZone(data.zone);
  }
  if ("notes" in data) {
    assertValidNotes(data.notes);
  }

  if ("buyer_type_id" in data) {
    assertSingleSelectGuard(
      data.buyer_type_id,
      previous?.buyer_type_id,
      catalogues.buyerTypes,
    );
  }
  if ("credit_type_id" in data) {
    assertSingleSelectGuard(
      data.credit_type_id,
      previous?.credit_type_id,
      catalogues.creditTypes,
    );
  }
  if ("timeline_id" in data) {
    assertSingleSelectGuard(
      data.timeline_id,
      previous?.timeline_id,
      catalogues.timelines,
    );
  }
  if ("motivator_ids" in data) {
    assertArraySelectGuards(
      data.motivator_ids ?? [],
      previous?.motivator_ids ?? [],
      catalogues.motivators,
    );
  }
  if ("objection_ids" in data) {
    assertArraySelectGuards(
      data.objection_ids ?? [],
      previous?.objection_ids ?? [],
      catalogues.objections,
    );
  }
  if ("compared_project_ids" in data) {
    assertArraySelectGuards(
      data.compared_project_ids ?? [],
      previous?.compared_project_ids ?? [],
      catalogues.projects,
      {
        archivedMessage: PROJECT_ARCHIVED_MESSAGE,
        isArchived: (row) => row.archived === true,
      },
    );
  }
}

// ---------------------------------------------------------------------------
// (6) Immutable single/multi-select toggles
// ---------------------------------------------------------------------------

/**
 * DATA CONTRACT EF (6): tapping an unselected chip selects it (replacing the
 * previous one), tapping the selected chip clears it. Never mutates `currentId`.
 */
export function selectSingleOption(
  currentId: Identifier | null | undefined,
  optionId: Identifier,
): Identifier | null {
  return currentId != null && String(currentId) === String(optionId)
    ? null
    : optionId;
}

/**
 * DATA CONTRACT EF (6): tapping toggles; at 10 selected, an unselected option
 * is a no-op (the UI disables the tap, this is the safety net). Appends on
 * select, removes on deselect (stored order = selection order). Never
 * mutates `currentIds`.
 */
export function toggleMultiOption(
  currentIds: readonly Identifier[],
  optionId: Identifier,
): Identifier[] {
  const isSelected = currentIds.some((id) => String(id) === String(optionId));
  if (isSelected) {
    return currentIds.filter((id) => String(id) !== String(optionId));
  }
  if (currentIds.length >= MAX_ARRAY_OPTIONS) {
    return [...currentIds];
  }
  return [...currentIds, optionId];
}

// ---------------------------------------------------------------------------
// (6) Picker option lists
// ---------------------------------------------------------------------------

export interface ProfilePickerOption {
  id: Identifier;
  label: string;
  /** Shown with the hint 'archivada' / 'archivado'. */
  archived: boolean;
}

function comparePositionThenId(
  a: { position: number; id: Identifier },
  b: { position: number; id: Identifier },
): number {
  if (a.position !== b.position) return a.position - b.position;
  return String(a.id).localeCompare(String(b.id), undefined, {
    numeric: true,
  });
}

/**
 * DATA CONTRACT EF (6): the non-archived options of a catalogue by
 * `(position, id)`, plus any archived option the profile already holds
 * (flagged, appended after the active ones).
 */
export function buildCatalogueOptions(
  options: readonly ProfileOption[],
  heldIds: readonly Identifier[],
): ProfilePickerOption[] {
  const heldSet = new Set(heldIds.map((id) => String(id)));
  const active = options
    .filter((option) => option.archived_at == null)
    .slice()
    .sort(comparePositionThenId);
  const heldArchived = options
    .filter(
      (option) => option.archived_at != null && heldSet.has(String(option.id)),
    )
    .slice()
    .sort(comparePositionThenId);

  return [...active, ...heldArchived].map((option) => ({
    id: option.id,
    label: option.label,
    archived: option.archived_at != null,
  }));
}

/**
 * DATA CONTRACT EF (6): projects with `archived = false` by name, plus any
 * archived project already held (flagged, appended after the active ones),
 * regardless of `compliance_pack_status` (comparing is not assigning).
 */
export function buildProjectOptions(
  projects: readonly Project[],
  heldIds: readonly Identifier[],
): ProfilePickerOption[] {
  const heldSet = new Set(heldIds.map((id) => String(id)));
  const byName = (a: Project, b: Project) => a.name.localeCompare(b.name);
  const active = projects
    .filter((project) => !project.archived)
    .slice()
    .sort(byName);
  const heldArchived = projects
    .filter((project) => project.archived && heldSet.has(String(project.id)))
    .slice()
    .sort(byName);

  return [...active, ...heldArchived].map((project) => ({
    id: project.id,
    label: project.name,
    archived: project.archived,
  }));
}

// ---------------------------------------------------------------------------
// (6) "{n} de 15" completeness count
// ---------------------------------------------------------------------------

export const PROFILE_DIMENSIONS_TOTAL = 15;

/**
 * DATA CONTRACT EF (6): how many of the 15 profile dimensions are answered --
 * "presupuesto" counts as one dimension when either bound is set, "quién
 * decide" comes from `deal.posi_decision_maker` (never duplicated into a
 * second field), a `bedrooms` of 0 ("Estudio") and a `sells_other_property`
 * of `false` both count as answered (not-null, not truthy).
 */
export function countProfileDimensions(
  profile: Partial<DealProfile> | null | undefined,
  deal: Pick<Deal, "posi_decision_maker"> | null | undefined,
): number {
  const p = profile ?? {};
  const dimensions = [
    p.buyer_type_id != null,
    p.budget_min != null || p.budget_max != null,
    p.credit_type_id != null,
    p.down_payment != null,
    p.monthly_payment != null,
    !isBlank(p.zone),
    p.property_type != null,
    p.bedrooms != null,
    p.timeline_id != null,
    (p.motivator_ids?.length ?? 0) >= 1,
    (p.objection_ids?.length ?? 0) >= 1,
    !isBlank(deal?.posi_decision_maker),
    p.sells_other_property != null,
    p.interest_level != null,
    (p.compared_project_ids?.length ?? 0) >= 1,
  ];
  return dimensions.filter(Boolean).length;
}

/** "{n} de 15". */
export function formatProfileDimensionsCount(count: number): string {
  return `${count} de ${PROFILE_DIMENSIONS_TOTAL}`;
}

// ---------------------------------------------------------------------------
// (7) CSV row mapping
// ---------------------------------------------------------------------------

export interface DealProfileCsvRow {
  profile_buyer_type: string;
  profile_budget_min: number | "";
  profile_budget_max: number | "";
  profile_credit_type: string;
  profile_down_payment: number | "";
  profile_monthly_payment: number | "";
  profile_zone: string;
  profile_property_type: string;
  profile_bedrooms: number | "";
  profile_timeline: string;
  profile_motivators: string;
  profile_objections: string;
  profile_sells_other_property: string;
  profile_interest_level: string;
  profile_compared_projects: string;
  profile_notes: string;
}

const EMPTY_CSV_ROW: DealProfileCsvRow = {
  profile_buyer_type: "",
  profile_budget_min: "",
  profile_budget_max: "",
  profile_credit_type: "",
  profile_down_payment: "",
  profile_monthly_payment: "",
  profile_zone: "",
  profile_property_type: "",
  profile_bedrooms: "",
  profile_timeline: "",
  profile_motivators: "",
  profile_objections: "",
  profile_sells_other_property: "",
  profile_interest_level: "",
  profile_compared_projects: "",
  profile_notes: "",
};

function labelById(
  id: Identifier | null | undefined,
  options: readonly ProfileOption[],
): string {
  if (id == null) return "";
  const found = options.find((option) => String(option.id) === String(id));
  return found ? found.label : "";
}

function joinLabelsByIds(
  ids: readonly Identifier[],
  options: readonly ProfileOption[],
): string {
  return ids
    .map((id) => labelById(id, options))
    .filter((label) => label !== "")
    .join("; ");
}

function joinProjectNamesByIds(
  ids: readonly Identifier[],
  projects: readonly Project[],
): string {
  return ids
    .map((id) => {
      const found = projects.find(
        (project) => String(project.id) === String(id),
      );
      return found ? found.name : "";
    })
    .filter((name) => name !== "")
    .join("; ");
}

/**
 * DATA CONTRACT EF (7): the leads CSV row mapping for the 16 `profile_*`
 * columns -- catalogue/project ids become labels/names (archived ones
 * included, since the profile already holds them), multi-values joined with
 * '; ' in stored order, fixed value sets and booleans carry their stored
 * value, amounts as plain numbers, every cell blank for a lead without a
 * profile.
 */
export function mapDealProfileToCsvRow(
  profile: DealProfile | null | undefined,
  catalogues: DealProfileCatalogues,
): DealProfileCsvRow {
  if (!profile) return { ...EMPTY_CSV_ROW };

  return {
    profile_buyer_type: labelById(profile.buyer_type_id, catalogues.buyerTypes),
    profile_budget_min: profile.budget_min ?? "",
    profile_budget_max: profile.budget_max ?? "",
    profile_credit_type: labelById(
      profile.credit_type_id,
      catalogues.creditTypes,
    ),
    profile_down_payment: profile.down_payment ?? "",
    profile_monthly_payment: profile.monthly_payment ?? "",
    profile_zone: profile.zone ?? "",
    profile_property_type: profile.property_type ?? "",
    profile_bedrooms: profile.bedrooms ?? "",
    profile_timeline: labelById(profile.timeline_id, catalogues.timelines),
    profile_motivators: joinLabelsByIds(
      profile.motivator_ids ?? [],
      catalogues.motivators,
    ),
    profile_objections: joinLabelsByIds(
      profile.objection_ids ?? [],
      catalogues.objections,
    ),
    profile_sells_other_property:
      profile.sells_other_property == null
        ? ""
        : String(profile.sells_other_property),
    profile_interest_level: profile.interest_level ?? "",
    profile_compared_projects: joinProjectNamesByIds(
      profile.compared_project_ids ?? [],
      catalogues.projects,
    ),
    profile_notes: profile.notes ?? "",
  };
}
